import { faChevronDown } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import * as React from 'react'

import { cn } from '@/lib/utils'

// Recuo horizontal das faixas: 8px no mobile/tablet e 40px no desktop —
// os mesmos do UIKIT (px-[8px] no grid 4col, px-[40px] no 12col).
const sectionPadding = 'px-[var(--spacing-scale-base)] lg:px-[var(--spacing-scale-5x)]'

type FooterProps = React.ComponentProps<'footer'> & {
  // Paleta invertida (fundo claro) — mapeia .br-footer.inverted.
  inverted?: boolean
  // Remove a borda superior — mapeia .br-footer.no-divider.
  noDivider?: boolean
}

function Footer({ className, inverted = false, noDivider = false, ...props }: FooterProps) {
  return (
    <footer
      data-slot="footer"
      // .br-footer aplica paleta "dark" por padrão (fundo escuro) e "light"
      // quando invertido — ambas vêm de token, nada de cor literal.
      data-inverted={inverted ? 'true' : undefined}
      className={cn(
        'flex flex-col',
        inverted
          ? [
              'bg-[var(--background-light)] text-[var(--color-light)]',
              '[--interactive:var(--interactive-light)]',
            ]
          : [
              'bg-[var(--background-dark)] text-[var(--color-dark)]',
              // Sobre fundo escuro o DS troca o interativo (blue-warm-20) e usa
              // o próprio texto como cor de divisor — é o que o Figma mostra.
              '[--interactive:var(--interactive-dark)]',
              '[--border-color:var(--color-dark)]',
            ],
        noDivider ? 'border-t-0' : '[border-top:var(--surface-width-sm)_solid_var(--border-color)]',
        className
      )}
      {...props}
    />
  )
}

// Marca/logo do órgão. Limita a altura como o DS (--footer-logo-height: 48px).
function FooterLogo({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="footer-logo"
      className={cn(
        sectionPadding,
        'pt-[var(--spacing-scale-6x)]',
        // .br-footer .logo img { max-height: 48px; max-width: 180px }
        '[&_img]:max-h-[48px] [&_img]:max-w-[180px]',
        className
      )}
      {...props}
    />
  )
}

// Grade de colunas de links. Sanfona empilhada até o tablet e 6 colunas no
// desktop (cada coluna ocupa 2/12 do grid, como no UIKIT).
function FooterColumns({ className, ...props }: React.ComponentProps<'nav'>) {
  return (
    <nav
      data-slot="footer-columns"
      className={cn(
        sectionPadding,
        'pt-[var(--spacing-scale-6x)]',
        'grid grid-cols-1 gap-0',
        'lg:grid-cols-6 lg:gap-[var(--spacing-scale-3x)]',
        className
      )}
      {...props}
    />
  )
}

type FooterColumnProps = Omit<React.ComponentProps<'details'>, 'title'> & {
  title: React.ReactNode
}

// Coluna de links colapsável. Usa <details>/<summary> nativos: acessível por
// teclado, sem JS. Aberta por padrão; no desktop o UIKIT não mostra a seta e a
// lista fica sempre visível (forçada por CSS, mesmo se fechada no mobile).
function FooterColumn({ className, title, children, open = true, ...props }: FooterColumnProps) {
  return (
    <details
      data-slot="footer-column"
      open={open}
      className={cn(
        'group',
        // Divisor entre categorias — só na sanfona (mobile/tablet).
        '[border-bottom:var(--surface-width-sm)_solid_var(--border-color)]',
        'lg:border-b-0',
        // Desktop: a lista permanece visível mesmo com o <details> fechado.
        'lg:[&:not([open])>[data-slot=footer-column-links]]:block',
        className
      )}
      {...props}
    >
      <summary
        data-slot="footer-column-title"
        className={cn(
          'flex cursor-pointer list-none items-center justify-between',
          'gap-[var(--spacing-scale-2x)]',
          // .title: down-01, semibold e caixa alta.
          '[font-size:var(--font-size-scale-down-01)] uppercase',
          '[font-weight:var(--font-weight-semi-bold)]',
          'px-[var(--spacing-scale-2x)] py-[var(--spacing-scale-baseh)]',
          // Sanfona aberta: divisor separando o cabeçalho dos links.
          'group-open:[border-bottom:var(--surface-width-sm)_solid_var(--border-color)]',
          'lg:pointer-events-none lg:cursor-default lg:[border-bottom:0]',
          'lg:px-[var(--spacing-scale-base)] lg:py-[var(--spacing-scale-2xh)]',
          // Foco tracejado do DS.
          'outline-none focus-visible:[outline:var(--focus-width)_dashed_var(--focus-color)] focus-visible:[outline-offset:var(--focus-offset)]',
          // Esconde o marcador nativo (a seta é o chevron do Font Awesome).
          '[&::-webkit-details-marker]:hidden'
        )}
      >
        {title}
        {/* A seta só existe na sanfona; no desktop o UIKIT não a mostra. */}
        <span
          aria-hidden="true"
          className={cn(
            'shrink-0 text-[color:var(--interactive)]',
            '[font-size:var(--font-size-scale-up-02)]',
            // Aberta → chevron-up (o UIKIT inverte a seta).
            'group-open:rotate-180',
            'lg:hidden'
          )}
        >
          <FontAwesomeIcon icon={faChevronDown} />
        </span>
      </summary>
      <ul
        data-slot="footer-column-links"
        className="m-0 flex list-none flex-col p-0 pb-[var(--spacing-scale-baseh)] lg:pb-0"
      >
        {children}
      </ul>
    </details>
  )
}

// Item de link de uma coluna. Composável: por padrão um <li><a>.
function FooterLink({ className, children, ...props }: React.ComponentProps<'a'>) {
  return (
    <li className="m-0">
      <a
        data-slot="footer-link"
        className={cn(
          'block [font-size:var(--font-size-scale-base)]',
          'px-[var(--spacing-scale-2x)] py-[var(--spacing-scale-base)]',
          'lg:px-[var(--spacing-scale-base)] lg:py-[var(--spacing-scale-2x)]',
          'text-[color:var(--interactive)] no-underline hover:underline',
          'outline-none focus-visible:[outline:var(--focus-width)_dashed_var(--focus-color)] focus-visible:[outline-offset:var(--focus-offset)]',
          className
        )}
        {...props}
      >
        {children}
      </a>
    </li>
  )
}

// Faixa secundária: redes sociais à esquerda e assinaturas à direita na mesma
// linha a partir do desktop (info-secondary no UIKIT).
function FooterBottom({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="footer-bottom"
      className={cn(
        sectionPadding,
        'py-[var(--spacing-scale-6x)]',
        'flex flex-col gap-[var(--spacing-scale-3x)]',
        'lg:flex-row lg:items-end lg:justify-between',
        className
      )}
      {...props}
    />
  )
}

type FooterSocialProps = React.ComponentProps<'div'> & {
  title?: React.ReactNode
}

// Seção de redes sociais. Icon-agnóstico: os links com ícones/marcas são
// fornecidos por children (evita forçar @fortawesome/free-brands). Cada link
// vira um alvo circular de 48px, como o UIKIT.
function FooterSocial({
  className,
  title = 'Redes sociais',
  children,
  ...props
}: FooterSocialProps) {
  return (
    <div data-slot="footer-social" className={cn('border-0', className)} {...props}>
      <p
        data-slot="footer-social-title"
        className={cn(
          // .social-network-title: base, bold, caixa alta.
          '[font-size:var(--font-size-scale-base)] [font-weight:var(--font-weight-bold)]',
          'm-0 mb-[var(--spacing-scale-3x)] uppercase'
        )}
      >
        {title}
      </p>
      <div
        className={cn(
          'flex items-center gap-[var(--spacing-scale-3x)]',
          // Alvo circular de 48px com o ícone centralizado (.br-button.circle).
          '[&_a]:inline-flex [&_a]:size-[48px] [&_a]:items-center [&_a]:justify-center',
          '[&_a]:rounded-full [&_a]:text-[color:var(--interactive)]',
          '[&_a]:[font-size:var(--font-size-scale-up-02)] [&_a]:no-underline',
          '[&_a:hover]:[background-image:var(--hover-effect)]',
          '[&_a]:outline-none [&_a:focus-visible]:[outline:var(--focus-width)_dashed_var(--focus-color)] [&_a:focus-visible]:[outline-offset:var(--focus-offset)]'
        )}
      >
        {children}
      </div>
    </div>
  )
}

// Faixa de assinaturas (logos de parceria). Limita altura/largura como o DS.
function FooterAssigns({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="footer-assigns"
      className={cn(
        'flex items-center gap-[var(--spacing-scale-3x)] lg:justify-end',
        // .assigns img { max-height: 48px; max-width: 180px }
        '[&_img]:max-h-[48px] [&_img]:max-w-[180px]',
        className
      )}
      {...props}
    />
  )
}

// Barra de licença ao pé: divisor no topo, texto centralizado em down-01.
function FooterInfo({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="footer-info"
      className={cn(
        '[border-top:var(--surface-width-sm)_solid_var(--border-color)]',
        'px-[var(--spacing-scale-2x)] py-[var(--spacing-scale-2x)]',
        'text-center [font-size:var(--font-size-scale-down-01)]',
        className
      )}
      {...props}
    />
  )
}

export {
  Footer,
  FooterAssigns,
  FooterBottom,
  FooterColumn,
  FooterColumns,
  FooterInfo,
  FooterLink,
  FooterLogo,
  FooterSocial,
}
